import React, { useState } from 'react';
import { Search, PenTool as Tool } from 'lucide-react';
import { Staff, StaffFilter, Tool as ToolType, ExperienceLevel } from '../types';

interface StaffFilterPanelProps {
  data: Staff[];
  tools: ToolType[];
  onFilterChange: (filter: StaffFilter) => void;
}

export function StaffFilterPanel({ data, tools, onFilterChange }: StaffFilterPanelProps) {
  const [filter, setFilter] = useState<StaffFilter>({
    name: '',
    role: '',
    experienceLevel: '',
    selectedTools: [],
    yearJoined: ''
  });

  const uniqueRoles = React.useMemo(() => {
    const roleSet = new Set<string>();
    data.forEach(member => {
      if (member.role) {
        roleSet.add(member.role);
      }
    });
    return Array.from(roleSet).sort();
  }, [data]);

  const experienceLevels: ExperienceLevel[] = ['junior', 'mid-level', 'senior', 'lead'];
  const years = Array.from({ length: 9 }, (_, i) => (2017 + i).toString());

  const handleFilterChange = (key: keyof StaffFilter, value: string | string[]) => {
    const newFilter = { ...filter, [key]: value };
    setFilter(newFilter);
    onFilterChange(newFilter);
  };

  const toggleTool = (toolName: string) => {
    const selectedTools = filter.selectedTools.includes(toolName)
      ? filter.selectedTools.filter(t => t !== toolName)
      : [...filter.selectedTools, toolName];
    handleFilterChange('selectedTools', selectedTools);
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-xl font-semibold mb-4">Filters</h2>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Name</label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
              placeholder="Search staff..."
              value={filter.name}
              onChange={(e) => handleFilterChange('name', e.target.value)}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Role</label>
          <select
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            value={filter.role}
            onChange={(e) => handleFilterChange('role', e.target.value)}
          >
            <option value="">All Roles</option>
            {uniqueRoles.map(role => (
              <option key={role} value={role}>{role}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Experience Level</label>
          <select
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            value={filter.experienceLevel}
            onChange={(e) => handleFilterChange('experienceLevel', e.target.value as ExperienceLevel)}
          >
            <option value="">All Levels</option>
            {experienceLevels.map(level => (
              <option key={level} value={level}>
                {level.charAt(0).toUpperCase() + level.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Year Joined</label>
          <select
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            value={filter.yearJoined}
            onChange={(e) => handleFilterChange('yearJoined', e.target.value)}
          >
            <option value="">All Years</option>
            {years.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Tools</label>
          <div className="flex flex-wrap gap-2">
            {tools.map(tool => (
              <button
                key={tool.id}
                onClick={() => toggleTool(tool.name)}
                className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
                  filter.selectedTools.includes(tool.name)
                    ? 'bg-indigo-100 text-indigo-800'
                    : 'bg-gray-100 text-gray-700'
                }`}
              >
                <Tool className="w-4 h-4 mr-1" />
                {tool.name}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}